import React from 'react'
import { Link } from 'react-router-dom'
import { useI18nContext } from '../hooks/I18nContext'

const Buy: React.FC = () => {
  const { t, tRaw } = useI18nContext()

  const plans = {
    basic: {
      name: "GENIUS AI",
      price: t('buy.package.price'),
      originalPrice: t('buy.package.originalPrice'),
      features: tRaw('buy.package.features'),
      color: "pink",
      popular: true
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-blue-50 to-green-50">
      <div className="relative z-10">
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-6">
                {t('buy.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
                {t('buy.hero.subtitle')}
              </p>
            </div>
          </section>

          {/* Plan Selection */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-800 text-center mb-12">
                {t('buy.plans.title')}
              </h2>

              {/* Plan Card */}
              <div className="max-w-md mx-auto">
                <div className="bg-white rounded-2xl p-8 shadow-lg relative border-2 border-pink-500">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-pink-500 text-white text-sm font-bold px-4 py-2 rounded-full">
                      {t('buy.package.discount')}
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-3xl font-bold text-gray-800 mb-4">{t('buy.package.title')}</h3>
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-pink-500">{t('buy.package.price')}</span>
                      <div className="text-lg text-gray-500 line-through mt-2">{t('buy.package.originalPrice')}</div>
                      <div className="text-sm text-green-600 font-semibold mt-1">{t('buy.package.savings')}</div>
                    </div>
                    <ul className="space-y-3 mb-8 text-left">
                      {plans.basic.features.map((feature: string, index: number) => (
                        <li key={index} className="flex items-center">
                          <div className="w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center mr-3">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button className="w-full py-4 px-6 rounded-lg font-semibold text-lg bg-pink-500 text-white hover:bg-pink-600 transition-all duration-300">
                      {t('buy.package.buyButton')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Product Showcase */}
          <section className="py-16 px-8 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-800 text-center mb-12">
                {t('buy.showcase.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="text-center">
                  <div className="w-80 h-80 bg-gradient-to-br from-pink-200 to-blue-200 rounded-3xl mx-auto mb-8 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-48 h-48 bg-white rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg">
                        <svg className="w-24 h-24 text-pink-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-lg font-semibold text-gray-800">GENIUS AI</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-pink-50 rounded-2xl p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('buy.benefits.discount.title')}</h3>
                    <p className="text-gray-600">
                      {t('buy.benefits.discount.description')}
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-2xl p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('buy.benefits.shipping.title')}</h3>
                    <p className="text-gray-600">
                      {t('buy.benefits.shipping.description')}
                    </p>
                  </div>
                  
                  <div className="bg-green-50 rounded-2xl p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">{t('buy.benefits.warranty.title')}</h3>
                    <p className="text-gray-600">
                      {t('buy.benefits.warranty.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Purchase Process */}
          <section className="py-16 px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-800 text-center mb-12">
                {t('buy.process.title')}
              </h2>
              <div className="grid md:grid-cols-4 gap-8">
                {(tRaw('buy.process.steps') || []).map((item: any, index: number) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-pink-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{index + 1}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="bg-white rounded-3xl p-12 shadow-lg">
                <h3 className="text-3xl font-bold text-gray-800 mb-6">
                  {t('buy.cta.title')}
                </h3>
                <p className="text-xl text-gray-600 mb-8">
                  {t('buy.cta.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-4 rounded-lg transition-all duration-300 font-semibold text-lg">
                    {t('buy.cta.buyButton')}
                  </button>
                  <Link
                    to="/start"
                    className="bg-white hover:bg-gray-50 text-pink-500 px-8 py-4 rounded-lg transition-all duration-300 font-semibold text-lg border-2 border-pink-500"
                  >
                    {t('buy.cta.learnMoreButton')}
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default Buy
